import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RedirectCommand,
  ResolveFn,
  Router,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';
import StudentsApiService from '@shared/services/students/students-api.service';
import {
  FirstLessonRedirectDto,
  FirstLessonRedirectType,
} from '@shared/services/students/dto/first-lesson-redirect.dto';
import { ClientStateService } from '@shared/state/client-state.service';
import { TeacherSearchParams } from '@pages/teacher-search/enums/teacher-search-params.enum';
import { AppRoutesDefinitions } from '@app/app.routes';
import { i18n } from '@shared/language/i18n';

const i18nFollowUp = i18n.follow_up;

export const payFirstLessonResolver: ResolveFn<
  UrlTree | boolean | RedirectCommand
> = (
  route: ActivatedRouteSnapshot,
): Observable<UrlTree | boolean | RedirectCommand> => {
  const router = inject(Router);
  const studentsApi = inject(StudentsApiService);
  const modalService = inject(NzModalService);
  const translateService = inject(TranslateService);
  const clientState = inject(ClientStateService);

  const studentId = route.paramMap.get('studentId');
  const dashboardTree = router.parseUrl(`/${AppRoutesDefinitions.DASHBOARD}`);

  if (!studentId) {
    return of(dashboardTree);
  }

  return studentsApi.getFirstLessonRedirect(studentId).pipe(
    map((response: FirstLessonRedirectDto): UrlTree | boolean => {
      if (response.type === FirstLessonRedirectType.PAYMENT) {
        window.location.assign(response.paymentLink);
        return false;
      }

      if (response.type === FirstLessonRedirectType.PAYMENT_IN_PROGRESS) {
        modalService.info({
          nzTitle: translateService.instant(
            i18nFollowUp.payment_in_progress_title,
          ),
          nzContent: translateService.instant(
            i18nFollowUp.payment_in_progress_body,
          ),
          nzOkText: translateService.instant(
            i18nFollowUp.payment_in_progress_cta,
          ),
          nzCentered: true,
          nzOnOk: () => router.navigate([`/${AppRoutesDefinitions.DASHBOARD}`]),
        });

        return dashboardTree;
      }

      if (response.type === FirstLessonRedirectType.CHANGE_TEACHER) {
        clientState.followUpChangeTeacherData.set({
          studentId: response.studentId,
          workingClass: response.workingClass,
          subject: response.subject,
        });

        const modal = modalService.confirm({
          nzTitle: translateService.instant(i18nFollowUp.change_teacher_title),
          nzContent: translateService.instant(i18nFollowUp.change_teacher_body),
          nzIconType: 'exclamation-circle',
          nzOkText: translateService.instant(i18nFollowUp.change_teacher_cta),
          nzCancelText: translateService.instant(
            i18nFollowUp.change_teacher_back,
          ),
          nzCentered: true,
          nzOnOk: () => {
            const data = clientState.followUpChangeTeacherData();
            router.navigate([`/${AppRoutesDefinitions.TEACHER_SEARCH}`], {
              queryParams: {
                student_id: data?.studentId,
                isPrivate: true,
                workingClass: data?.workingClass,
                subject: data?.subject,
                [TeacherSearchParams.TEACHER_REPLACEMENT]: true,
              },
            });
          },
        });
        modal.afterClose.subscribe(() =>
          clientState.followUpChangeTeacherData.set(null),
        );

        return dashboardTree;
      }

      modalService.success({
        nzTitle: translateService.instant(
          i18nFollowUp.lesson_already_payed_title,
        ),
        nzContent: translateService.instant(
          i18nFollowUp.lesson_already_payed_body,
        ),
        nzOkText: translateService.instant(
          i18nFollowUp.lesson_already_payed_cta,
        ),
        nzCentered: true,
        nzOnOk: () => router.navigate([`/${AppRoutesDefinitions.DASHBOARD}`]),
        nzOnCancel: () =>
          router.navigate([`/${AppRoutesDefinitions.DASHBOARD}`]),
      });

      return dashboardTree;
    }),
    catchError((error) => {
      console.error('Failed to resolve first-lesson redirect', error);
      return of(dashboardTree);
    }),
  );
};
